/** Ценовые ряды и таблица глубины. Чистые функции от (рынок, размер, тик). */
import { DIR_SEEDS, MARKET_META, PROBE_SIZES } from './mock/constants'
import { rng } from './mock/rng'
import type { Asset, DepthRow, Market, PricePoint, TwoSidedQuote } from '../types'
import { grp } from '../lib/format'
import { parseDirection } from '../lib/orientation'

export const CHART_POINTS = 61

/**
 * Uniswap стабильно хуже Binance на 1–2 bps на каждой стороне и всегда
 * отстаёт по свежести: замерено min 1000 мс, p50 1568 мс, p95 1985 мс.
 * Отставание воспроизводится сдвигом ряда, а не сглаживанием: иначе на
 * экране Uniswap выглядел бы аккуратнее, чем он есть.
 */
const UNI_PREMIUM_BPS = 1.4
const UNI_LAG_POINTS = 3

const MARKET_BASE: Record<Market, Asset> = {
  'ETH-USDC': 'WETH',
  'ETH-USDT': 'WETH',
  'USDC-USDT': 'USDC',
}

const MARKET_QUOTE: Record<Market, Asset> = {
  'ETH-USDC': 'USDC',
  'ETH-USDT': 'USDT',
  'USDC-USDT': 'USDT',
}

/**
 * Две стороны нашей котировки, собранные из двух РАЗНЫХ направлений.
 *
 * Направление, где мейкер ПОЛУЧАЕТ базовый актив рынка, — это наш бид:
 * его цена уже выражена как quote за base. Обратное направление даёт цену в
 * обратной шкале, поэтому аск получается инверсией — единственное место, где
 * она нужна, и она идёт через orientation.ts, а не угадывается по величине.
 *
 * Ширина между сторонами и есть «запас до скрещивания»: как только bid
 * поднимается выше ask, произведение пары уходит за 1.0 и венью режет пару
 * целиком, а не одну её сторону.
 */
export function ourTwoSided(market: Market): TwoSidedQuote | null {
  const base = MARKET_BASE[market]
  const quote = MARKET_QUOTE[market]

  const fwd = DIR_SEEDS.find((d) => {
    const p = parseDirection(d.key)
    return p.base === base && p.quote === quote
  })
  const rev = DIR_SEEDS.find((d) => {
    const p = parseDirection(d.key)
    return p.base === quote && p.quote === base
  })
  if (!fwd || !rev) return null

  const bid = fwd.price
  const ask = 1 / rev.price
  const mid = Math.sqrt(bid * ask)

  return {
    bid,
    ask,
    spreadBps: ((ask - bid) / mid) * 10_000,
    bidDirection: fwd.key,
    askDirection: rev.key,
  }
}

/**
 * Ряды графика. Ноль на оси — исполнимый мид Binance на выбранном размере.
 *
 * Наши две стороны едут вместе как жёсткая пара (это наш спред) и скользят
 * относительно полосы Binance, потому что уровни переиздаются раз в 1–5 с, а
 * книга биржи движется непрерывно. Когда наша сторона заезжает внутрь серой
 * полосы — мы котируем лучше биржи, то есть платим за поток.
 */
export function buildSeries(market: Market, tick: number): PricePoint[] {
  const meta = MARKET_META[market]
  const two = ourTwoSided(market)
  const r = rng(meta.seed + tick)

  // Половина НАШЕГО спреда — она же половина запаса до скрещивания пары.
  const halfSpreadBps = two ? two.spreadBps / 2 : 1

  // Дрейф мида Binance относительно нашего — то, из-за чего наши стороны
  // оказываются то внутри, то снаружи биржевого спреда.
  const step = meta.driftStepBps
  const bound = Math.max(2, step * 5)
  const drift: number[] = []
  let v = step
  for (let i = 0; i < CHART_POINTS; i++) {
    v = Math.max(-bound, Math.min(bound, v + (r() - 0.5) * step * 2))
    drift.push(v)
  }

  const out: PricePoint[] = []
  for (let i = 0; i < CHART_POINTS; i++) {
    const binHalf = meta.halfSpreadBps * (0.9 + r() * 0.25)

    // Уровни переиздаются ступенями раз в 1–5 с, а книга биржи движется
    // непрерывно. Поэтому наша котировка отъезжает от мида между публикациями
    // и возвращается на публикации — пила, а не гладкая линия.
    const republishAt = Math.floor(i / 7) * 7
    const rq = rng(meta.seed * 31 + tick + republishAt)
    // Скидка задана ПО НАПРАВЛЕНИЮ, поэтому стороны сдвигаются независимо и
    // наша полоса не центрирована относительно нуля.
    const bidSkew = (rq() - 0.5) * 0.9
    const askSkew = (rq() - 0.5) * 0.9

    const rel = -(drift[i] - drift[republishAt])
    const bidBps = rel - halfSpreadBps + bidSkew
    const askBps = rel + halfSpreadBps + askSkew

    // Uniswap отстаёт: его цена — это мид биржи примерно секунду назад.
    const lagged = drift[Math.max(0, i - UNI_LAG_POINTS)]
    const uniCentre = lagged - drift[i]
    const uniHalf = binHalf + UNI_PREMIUM_BPS

    out.push({
      t: i,
      ourBidBps: bidBps,
      ourAskBps: askBps,
      binanceHalfSpreadBps: binHalf,
      uniBidBps: uniCentre - uniHalf,
      uniAskBps: uniCentre + uniHalf,
    })
  }
  return out
}

/** Индексы точек, на которых стоят отметки выигранных сделок. */
export const FILL_MARKS = [12, 34, 51]

/**
 * Таблица глубины. Каждая строка Binance — VWAP-свип книги на этот размер,
 * а не топ книги.
 *
 * Uniswap здесь НЕ квотится по одному вызову на строку. Наш квотер веером
 * опрашивает четыре фи-тира через on-chain Quoter, то есть одна цена стоит
 * 4 eth_call, а вся таблица — 216 вызовов на обновление. Правильный источник
 * — состояние пула, прочитанное раз в блок, и кривая, посчитанная локально;
 * подробности и цифры бюджета в README, раздел «Uniswap и бюджет RPC».
 */
export function buildDepth(market: Market): DepthRow[] {
  const meta = MARKET_META[market]
  const isEth = meta.ref > 100
  return PROBE_SIZES[market].map((size) => {
    const usd = isEth ? size * meta.ref : size
    const slip = meta.halfSpreadBps + Math.log10(1 + usd / 900) * (isEth ? 0.55 : 0.3)
    // Ликвидность Uniswap тоньше, поэтому его проскальзывание растёт быстрее.
    const uniSlip = slip + UNI_PREMIUM_BPS + Math.log10(1 + usd / 900) * (isEth ? 0.35 : 0.2)
    return {
      size,
      sizeLabel: isEth ? `${size} ETH` : `${grp(size)} USDC`,
      binanceBid: meta.ref * (1 - slip / 10_000),
      binanceAsk: meta.ref * (1 + slip / 10_000),
      uniBid: meta.ref * (1 - uniSlip / 10_000),
      uniAsk: meta.ref * (1 + uniSlip / 10_000),
      selected: isEth ? size === 1 : size === 10_000,
    }
  })
}

export function usdEquivalent(market: Market, size: number): number {
  const meta = MARKET_META[market]
  return meta.ref > 100 ? size * meta.ref : size
}
